import { useRef, useState } from 'react';
import { MessageSquare, MessageSquareText } from 'lucide-react';
import { Popover } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

// VISITS-3 (#176): per-parameter comment affordance. Desktop: hidden until
// the row is hovered (parent must have the `group` class); the button never
// hides once a comment exists, so its "has comment" state stays visible at
// rest. Touch: always shown (tap-to-open replaces hover, same as
// UnitDotPopover's pattern elsewhere in this codebase) since there's no
// hover state to reveal it.
export function ParameterCommentButton({
  comment,
  onSave,
  readOnly,
}: {
  comment: string;
  onSave: (text: string) => void | Promise<void>;
  readOnly?: boolean;
}) {
  const { t } = useT();
  const isCoarse = useMediaQuery('(pointer: coarse)');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(comment);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasComment = comment.trim().length > 0;

  const handleOpen = () => {
    setDraft(comment);
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        aria-label={hasComment ? t('visits.editComment') : t('visits.addComment')}
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-md transition-opacity',
          hasComment ? 'text-primary' : 'text-muted-foreground',
          !hasComment && !isCoarse && 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
        )}
      >
        {hasComment ? <MessageSquareText className="size-4" /> : <MessageSquare className="size-4" />}
      </button>
      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} align="end">
        <div className="w-64 space-y-2 p-3">
          {readOnly ? (
            <p className="whitespace-pre-wrap text-sm">{comment || t('visits.noComment')}</p>
          ) : (
            <>
              <Textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder={t('visits.commentPlaceholder')}
                className="min-h-[70px] text-sm"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {t('common.save')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Popover>
    </>
  );
}
